import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import imageUrlBuilder from '@sanity/image-url';

import { Alert } from '@tmap/mmm-style-guide/src/Alert';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Card } from '@tmap/mmm-style-guide/src/Card';
import { Chip } from '@tmap/mmm-style-guide/src/Chip';
import { Favorite, Share, MapsUgcOutlined } from '@tmap/mmm-style-guide/src/Icon';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Hyperlink } from '@tmap/mmm-style-guide/src/Hyperlink';
import { IconButton } from '@tmap/mmm-style-guide/src/IconButton';
import { MenuItem } from '@tmap/mmm-style-guide/src/MenuItem';
import { Select } from '@tmap/mmm-style-guide/src/Select';
import { Skeleton } from '@tmap/mmm-style-guide/src/Skeleton';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { useMediaQuery } from '@tmap/mmm-style-guide/src/useMediaQuery';
import { useTheme } from '@tmap/mmm-style-guide/src/Theming';

import useDocumentCache from '@tmap/mmm-core/redux/documentCache/useDocumentCache';
import useView from '@tmap/mmm-core/redux/views/useView';
import isEmptyish from '@tmap/mmm-core/isEmptyish';
import { fromKey, toKey, types } from '@tmap/mmm-core/entityKey';
import miniHandlebars from '@tmap/mmm-core/miniHandlebars';
import locationLabelText from '@tmap/mmm-core/locationLabelText';
import truncateText from '@tmap/mmm-core/truncateText';

import client from '../../../client';
import FullWrapper from '../../../layouts/fullWrapper';
import ColorBlocks from '../../../layouts/colorBlocks';
import Metatags from '../../../components/metatags';
import CardAd from '../../../components/cardAd';
import PlaceInfoItem, { itemMappings } from '../../../components/offers/placeInfoItem';
import CommunityFactoid from '../../../components/offers/communityFactoid';
import CommunityOffers from '../../../components/offers/communityOffers';
import FavoriteButton from '../../../components/favoriteButton';
import SanityGallery from '../../../components/fullscreenGallery';
import CommunityAwards from '../../../components/communityAwards';
import ModeratorBanner from '../../../components/communityContent/moderatorBanner';
import PendingCTA from '../../../components/communityContent/pendingCTA';
import PinnedContent from '../../../components/communityContent/pinnedContent';
import PostGridItems from '../../../components/communityContent/postGridItems';
import FullWidthPostCTA from '../../../components/communityContent/fullWidthPostCta';
import CostOfLivingWidget from '../../../components/costOfLivingWidget';
import CommunityCounts from '../../../components/communityCard/shared/counts';
import InfiniteScroll from '../../../components/infiniteScroll';
import ContentBlocks from '../../../components/communityEditor/blocks/contentBlocks';
import ContributionCardWide from '../../../components/communityContent/contributionCardWide';
import ContributionCard from '../../../components/communityContent/contributionCard';
import EditorBannerCta from '../../../components/communityEditor/bannerCta';
import ResponsiveBottomDrawer from '../../../components/responsiveBottomDrawer';

import {
  getHashtagTotals,
  getPaginatedCommunityContributions,
  getPaginatedCommunityContributionsView,
  getPaginatedCommunityQuestionsView,
  getPendingContributions,
  getPinnedContributions,
  getPinnedContributionsView,
  getTopicTotals,
} from '../../../actions/contributions';
import { getCommunityBlocks } from '../../../actions/communityEditor';
import { CONTRIBUTIONS_CACHE, QUESTIONS_CACHE } from '../../../actions/cacheIndex';

import isUserModerator from '../../../lib/community/isUserModerator';
import RefreshPinnedContext from '../../../lib/community/refreshPinnedContext';
import CommunityContext from '../../../lib/community/communityContext';
import setCacheHeaders from '../../../lib/setCacheHeaders';
import sentenceCase from '../../../lib/sentenceCase';
import { hydrateBlock } from '../../../lib/communityEditor';

import useCurrentUser from '../../../hooks/useCurrentUser';
import useUserListItem from '../../../hooks/useUserListItem';
import { addUserFavorite } from '../../../actions/user';
import useRequireAuth from '../../../hooks/useRequireAuth';
import OfferProgressContext from '../../../components/incentiveCard/offerProgressContext';

const RealtorCard = dynamic(() => import('../../../components/realtorCard'));
const DynamicShareButton = dynamic(() => import('../../../components/shareButton'));

const builder = imageUrlBuilder(client);

const BreadcrumbWrapper = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  paddingTop: theme.spacing(3),
}));

const OfferTagContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

const CommunityHeader = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse',
  },
}));

const GalleryContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginLeft: '-24px',
    '.gallery-border-radius, img, .react-player__preview': {
      width: '100vw',
      borderRadius: '0!important',
    },
    '.MuiChip-root': {
      right: theme.spacing(-2),
    },
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.primary.veryLight : theme.palette.primary.dark,
  padding: theme.spacing(2, 3),
  borderRadius: '12px',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6),
  },
}));

const BorderedQuestionGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  // FYI (may have to change when grid changes): nth last of type 2 bc button is always last in grid
  '&:not(:nth-last-of-type(2))': {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
}));

const FavoriteFooterButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
  '& .MuiButton-root': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

function CommunityPage(props) {
  const {
    initialContributions = [],
    initialQuestions = [],
    community,
    pageSettings,
    communityBlocks,
    pinnedContributions = [],
    suggestedTopics,
    offerProgressSettings,
  } = props;

  const canonicalUrl = `${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/moving-living/${community.slug.current}`;
  const router = useRouter();
  const user = useCurrentUser();
  const entityKey = toKey(types.COMMUNITY, community._id);
  const favorite = useUserListItem({ entityKey });
  const isFavorite = (!!favorite && favorite?.status === 'active');
  const { requireAuth } = useRequireAuth();
  const dispatch = useDispatch();

  const handleFavoriteClick = () => {
    requireAuth(() => {
      dispatch(addUserFavorite(entityKey));
    }, { returnTo: `/favorites/continue?id=${entityKey}&url=/moving-living/${community.slug.current}` });
  };

  const { page: pageParam = 1, qpage: qPageParam = 1 } = router.query;

  // contribution paging
  const initialPage = Math.max(+pageParam, 1);
  const [page, setPage] = useState(initialPage);
  const limit = 6;
  const offset = ((page || 1) - 1) * limit;

  // question paging
  const [qPage, setQPage] = useState(Math.max(+qPageParam, 1));
  const qLimit = 3;
  const qOffset = ((qPage || 1) - 1) * qLimit;

  const [sortBy, setSortBy] = useState('new');
  const [newlyPinnedContributionIds, setNewlyPinnedContributionIds] = useState([]);
  const [showFullDescription, setShowFullDescription] = useState(false);

  // avoiding useCurrentUser here to avoid an unnecessary rerender for non-role profile changes
  const userRoles = useSelector((state) => state?.users?.current?.roles, shallowEqual);
  const userId = useSelector((state) => state?.users?.current?.userId, shallowEqual);

  const contributionFilters = useMemo(() => ({ communityId: community._id }), [community._id]);
  const questionFilters = useMemo(() => ({ communityId: community._id, type: 'question' }), [community._id]);

  const [contributionIds, view] = useView(
    ['contributions', community._id, sortBy],
    getPaginatedCommunityContributionsView(limit, offset, sortBy, contributionFilters),
    { page, squashPages: true },
  );
  const contributions = useDocumentCache(
    CONTRIBUTIONS_CACHE,
    contributionIds,
  ).filter((contrib) => !contrib.deleted);
  const contributionsToDisplay = (contributions?.length > 0
    ? contributions
    : initialContributions.docs).filter((contrib) => !contrib.deleted);

  const [questionIds, qView] = useView(
    ['questions', community._id],
    getPaginatedCommunityQuestionsView(
      qLimit,
      qOffset,
      sortBy,
      questionFilters,
    ),
    { page: qPage, squashPages: true },
  );
  const questions = useDocumentCache(
    QUESTIONS_CACHE,
    questionIds,
  ).filter((contrib) => !contrib.deleted);
  const questionsToDisplay = (questions?.length > 0
    ? questions
    : initialQuestions.docs).filter((contrib) => !contrib.deleted);

  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));

  const [pinnedContributionIds, pinnedView] = useView(['pinnedContributions', community._id], getPinnedContributionsView(contributionFilters));

  // in case a single post was repeatedly pinned/unpinned, dedupe the list of IDs to look up
  const cachedPinnedContributions = useDocumentCache(
    CONTRIBUTIONS_CACHE,
    Array.from(new Set([...pinnedContributionIds, ...newlyPinnedContributionIds])),
  );
  const pinnedToDisplay = (
    pinnedView.isReady
      ? cachedPinnedContributions
      : pinnedContributions
  )
    .filter((contrib) => !contrib.deleted && contrib.pinned)
    .slice(0, 3);

  const successRef = useRef(null);

  // track which posts were pinned/unpinned so that we know which ones to fetch from cache
  const updatePinned = useCallback((contribution) => {
    setNewlyPinnedContributionIds([contribution._id, ...newlyPinnedContributionIds]);
  }, [newlyPinnedContributionIds]);

  const [pendingContributionIds] = useView(['pendingContributions', community._id], getPendingContributions(
    userId,
    contributionFilters,
  ));

  const [path] = (typeof window !== 'undefined' ? router.asPath.split('#') : []);
  const handleOpen = (typeof window !== 'undefined' ? () => router.push(`/moving-living/${community.slug.current}/post`) : () => { });
  const handleNewPage = useCallback(() => setPage(page + 1), [page]);

  const handleSort = useCallback((e) => {
    setSortBy(e.target.value);
    setPage(1);
  }, []);

  const handleSeeMoreQuestions = useCallback(
    () => {
      setQPage(qPage + 1);
    },
    [qPage],
  );

  const communityRole = (userRoles ?? []).find((role) => fromKey(role).id === community._id);
  const userIsBlocked = communityRole === toKey(types.BLOCKED, community._id);
  const userIsModerator = isUserModerator({ roles: userRoles }, community._id);
  const preapproved = !userIsBlocked && (userRoles?.includes('employee') || Boolean(communityRole));

  // only allow the user to create new posts if
  // 1. they're not blocked
  // 2. they don't have any pending posts OR they're already preapproved
  const allowPosting = !userIsBlocked && (preapproved || isEmptyish(pendingContributionIds));

  const breadcrumbs = [
    { url: '/moving-living', text: 'Communities' },
    { text: community.formattedLocationText },
  ];

  const description = community?.blockDescription || community?.featuredDescription
    || community?.longDescription || '';
  const [textToDisplay, hasMoreText] = truncateText(description, 485);

  const maxOfferValue = community.offers.length === 1
    ? `$${new Intl.NumberFormat().format(community.offers[0].totalValue)}`
    : `Offers up to $${new Intl.NumberFormat().format(Math.max(...(community.offers || []).map((o) => o.totalValue)))}`;
  const useContainedApplyButton = useMediaQuery(useTheme().breakpoints.down('sm'));

  const heroImages = community?.sizzleReel
    ? [community.sizzleReel, ...community.galleryImages]
    : community?.galleryImages;

  const offerUrl = community?.offers?.length === 1
    ? `/get-paid/${encodeURIComponent(community.offers[0].slug.current)}`
    : `${path}#offers`;

  const [
    blocksBucketA = [],
    blocksBucketB = [],
    blocksBucketC = [],
  ] = communityBlocks.reduce((buckets, item, index) => {
    const bucketIndex = Math.min(Math.floor(index / (communityBlocks.length / 3)), 2);
    if (buckets[bucketIndex]) {
      buckets[bucketIndex].push(item);
      return buckets;
    }
    return [...buckets, [item]];
  }, []);

  return (
    <OfferProgressContext.Provider value={offerProgressSettings}>
      <CommunityContext.Provider value={community}>
        <RefreshPinnedContext.Provider value={updatePinned}>
          <Metatags
            title={miniHandlebars(pageSettings.title, community)}
            description={miniHandlebars(pageSettings.metaDescription, community)}
            canonical={canonicalUrl}
            next={initialContributions.paging.hasNextPage ? `${canonicalUrl}?page=${initialPage + 1}` : undefined}
            prev={initialContributions.paging.hasPrevPage ? `${canonicalUrl}?page=${initialPage - 1}` : undefined}
            {...(community.galleryImages?.length > 0
              ? { image: builder.image(community.galleryImages[0]).url() }
              : {}
            )}
            {...(community.sizzleReel ? { video: community.sizzleReel.url } : {})}
          />
          <FullWrapper>
            {userIsModerator && <ModeratorBanner community={community} />}
            <BreadcrumbWrapper color='textPrimary'>
              <ColorBlocks.Block spacing='none'>
                <Grid container alignItems='center'>
                  <Grid item xs>
                    {breadcrumbs.map(({ url, text }) => (
                      (url)
                        ? (
                          <Typography
                            variant='body1'
                            component='span'
                            color='textSecondary'
                            key={`breadcrumb-${text}`}
                            sx={{ color: (theme) => theme.palette.text.secondary }}
                          >
                            <Link href={url}>{text}</Link>
                            <span style={{ margin: '0 8px' }}>/</span>
                          </Typography>
                        )
                        : (
                          <Typography
                            variant='body1'
                            component='span'
                            color='textPrimary'
                            key={`breadcrumb-${text}`}
                          >
                            {text}
                          </Typography>
                        )
                    ))}
                  </Grid>
                </Grid>
              </ColorBlocks.Block>
            </BreadcrumbWrapper>
            {community.galleryImages?.length > 0 && (
              <ColorBlocks.Block spacing='small'>
                <GalleryContainer>
                  <SanityGallery mode='multi' mediaList={heroImages} title={community.formattedLocationText} aspectRatio={9 / 16} />
                </GalleryContainer>
              </ColorBlocks.Block>
            )}
            <ColorBlocks.Block spacing='none'>
              <Grid container spacing={2} sx={{ marginBottom: community?.offers?.length ? '0' : 6, mt: 0 }}>
                <Grid item container sm={7} md={8} sx={{ height: 'min-content' }}>
                  <CommunityHeader item container spacing={1}>
                    <Grid item xs>
                      <Typography variant='h4' component='h1' color='textPrimary' fontWeight={600} gutterBottom>
                        Life In {community.formattedLocationText}
                      </Typography>
                    </Grid>
                    <Grid item container xs='auto'>
                      {maxOfferValue && (
                        <OfferTagContainer item xs>
                          <Chip variant='pill' color='primary' label={maxOfferValue} sx={{ backgroundColor: 'secondary.veryDark', color: 'white' }} />
                        </OfferTagContainer>
                      )}
                      <Grid item>
                        <FavoriteButton Button={IconButton} entity={community} color='favorite' />
                        <DynamicShareButton
                          Button={IconButton}
                          url={canonicalUrl}
                          entityKey={entityKey}
                          ButtonProps={{ sx: { color: (theme) => theme.palette.grey[500] } }}
                        >
                          <Share />
                        </DynamicShareButton>
                      </Grid>
                    </Grid>
                  </CommunityHeader>
                  {textToDisplay && (
                    <Grid item container xs={12} direction='column'>
                      <Grid item xs>
                        <Typography variant='body2' color='textSecondary'>
                          {showFullDescription ? description : textToDisplay}
                          {hasMoreText && !showFullDescription && (
                            <>
                              &nbsp;
                              <Hyperlink component='button' variant='body2' fontWeight={500} onClick={() => setShowFullDescription(true)}>
                                Read More
                              </Hyperlink>
                            </>
                          )}
                          {showFullDescription && (
                            <>
                              &nbsp;
                              <Hyperlink component='button' variant='body2' fontWeight={500} onClick={() => setShowFullDescription(false)}>
                                Show Less
                              </Hyperlink>
                            </>
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                <Grid item container xs={12} sm={5} md={4} justifyContent='center'>
                  <Grid item container xs={12} md={9} direction='column' spacing={1.5}>
                    <CommunityCounts
                      community={community}
                      showApplyLink={community?.offers?.length > 0}
                      handleQuickApply={() => { router.push(offerUrl); }}
                      noApplyIcon={useContainedApplyButton}
                      applyArrowColor={useContainedApplyButton ? 'textPrimary' : 'textInverted'}
                      applyButtonProps={{
                        variant: useContainedApplyButton ? 'contained' : 'outlined',
                        fullWidth: true,
                        disableElevation: true,
                        sx: (th) => ({
                          marginBottom: th.spacing(2),
                          [th.breakpoints.down('sm')]: {
                            marginBottom: th.spacing(3),
                          },
                        }),
                      }}
                      responsiveBreakpoint='sm'
                    />
                  </Grid>
                </Grid>
              </Grid>
              {userIsModerator && pendingContributionIds.length > 0
                ? (
                  <Box sx={{ marginTop: 3 }}>
                    <PendingCTA numPending={pendingContributionIds.length} />
                  </Box>
                )
                : null}
            </ColorBlocks.Block>
            {community.offers.length > 0
              && (
                <ColorBlocks.Block overflow='sm'>
                  <Typography component='h3' variant='h6' id='offers'>Program(s) in {community.formattedLocationText}</Typography><br />
                  <CommunityOffers
                    offers={community.offers}
                    communitySettings={pageSettings}
                  />
                </ColorBlocks.Block>
              )}
            {!user._id && (
              <ColorBlocks.Interstitial colorTop='light' colorBottom='light'>
                <CardAd leadCopy={pageSettings.notificationBlockLead} />
              </ColorBlocks.Interstitial>
            )}
            {userIsModerator && (
              <EditorBannerCta community={community} />
            )}
            {blocksBucketA.length > 0 ? (
              <ContentBlocks blocks={blocksBucketA.map(hydrateBlock)} />
            ) : (
              <ColorBlocks.Block color='dim'>
                <Typography component='h3' variant='h6'>More About {community.locationText}</Typography>
                <br />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={community?.communityFactoid ? 6 : 12} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
                    {itemMappings.map((fact) => (
                      <PlaceInfoItem incentive={community} fact={fact} key={`infoItem-${fact.type}`} />
                    ))}
                  </Grid>
                  {community?.communityFactoid && (
                    <Grid item xs={12} sm={6}>
                      <CommunityFactoid
                        locationName={community.locationText}
                        factoid={community.communityFactoid}
                      />
                    </Grid>
                  )}
                </Grid>
              </ColorBlocks.Block>
            )}
            {(community.awards && community.awards.length > 0) && (
              <ColorBlocks.Block>
                <Typography variant='h6' textAlign='center' fontWeight={700}>Awards &amp; Press</Typography><br />
                <CommunityAwards awards={community.awards} />
              </ColorBlocks.Block>
            )}
            <ColorBlocks.Block>
              {questionsToDisplay && questionsToDisplay?.length > 0 && (
                <>
                  <Grid container alignItems='flex-end' spacing={2} marginBottom={4}>
                    <Grid item xs={12} md>
                      <Typography component='div' variant='h5' fontSize='24px' fontWeight={700} marginBottom={1}>Ask a question</Typography>
                      <Typography component='span' variant='body1' fontSize='16px' fontWeight={400} color='textSecondary'>Get answers to your questions from the people who live in {community.formattedLocationText}.</Typography>
                    </Grid>
                    <Grid item sx={{ textAlign: 'right' }}>
                      <Button
                        variant='outlined'
                        color='inverted'
                        sx={{ marginTop: { xs: 0.5, md: 1 } }}
                        href={`/moving-living/${community.slug.current}/questions`}
                      >Browse all questions
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid container direction='row'>
                    {questionsToDisplay.map((contribution, i) => (
                      <BorderedQuestionGrid item xs={12} key={contribution._id}>
                        {isMobile ? (
                          <ContributionCard
                            showCardBorder={false}
                            wantAnswerButton={false}
                            contribution={contribution}
                            entityUrl={`/c/${contribution.slug}`}
                            pageId='community'
                            placementId={`question-${i}`}
                          />
                        ) : (
                          <ContributionCardWide
                            showCardBorder={false}
                            wantAnswerButton={false}
                            contribution={contribution}
                            entityUrl={`/c/${contribution.slug}`}
                            pageId='community'
                            placementId={`question-${i}`}
                          />
                        )}
                      </BorderedQuestionGrid>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        disabled={!qView.paging?.hasNextPage}
                        loading={qView?.isLoading}
                        variant='outlined'
                        fullWidth
                        sx={{
                          marginY: { xs: 2, md: 0 },
                          '&.MuiButtonBase-root': {
                            backgroundColor: (theme) => theme.palette.grey[100],
                          },
                        }}
                        onClick={handleSeeMoreQuestions}
                      >See More
                      </Button>
                    </Grid>
                  </Grid>
                </>
              )}
              <StyledCard elevation={0} sx={{ backgroundColor: (theme) => theme.palette.primary.main, marginTop: '24px' }}>
                <Grid container alignItems='center' spacing={2}>
                  <Grid item xs={12} sm='auto' sx={{ textAlign: 'center' }}>
                    <MapsUgcOutlined
                      sx={{ fontSize: { xs: '36px', sm: '28px' }, color: (theme) => theme.palette.green.light, marginBottom: '-4px' }}
                    />
                  </Grid>
                  <Grid item xs sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    <Typography component='span' color='common.white' fontWeight={600} size='16px' lineHeight='28px'>Can’t quite find what you’re looking for?&nbsp;</Typography>
                    <Typography component='span' variant='subtitle1' color='common.white' fontWeight={400} size='16px' lineHeight='28px'> No worries, take the lead by posting a new question!</Typography>
                  </Grid>
                  <Grid item xs={12} sm='auto'>
                    <Button
                      variant='contained'
                      fullWidth
                      color='green'
                      sx={{
                        minWidth: '168px',
                        '&.MuiButtonBase-root': {
                          background: (theme) => theme.palette.green.light,
                        },
                      }}
                      href={`/moving-living/${community.slug.current}/post?type=question`}
                    >Ask a question
                    </Button>
                  </Grid>
                </Grid>
              </StyledCard>
            </ColorBlocks.Block>
            {blocksBucketB.length > 0 && (
              <ContentBlocks
                blocks={blocksBucketB.map(hydrateBlock)}
                indexOffset={blocksBucketA.length}
              />
            )}
            <ColorBlocks.Block>
              <CostOfLivingWidget place={community} />
            </ColorBlocks.Block>
            {(community.realtorBuyUrl || community.realtorRentUrl) && (
              <ColorBlocks.Block>
                <RealtorCard title={`Housing in ${community.formattedLocationText}`} doc={community} />
              </ColorBlocks.Block>
            )}
            {blocksBucketC.length > 0 && (
              <ContentBlocks
                blocks={blocksBucketC.map(hydrateBlock)}
                indexOffset={blocksBucketA.length + blocksBucketB.length}
                hideBottomBorder
              />
            )}
            <ColorBlocks.Block color='dim' overflow>
              <InfiniteScroll
                shouldLoadNewPage={!view.isLoading && view.paging?.hasNextPage}
                handleNewPage={handleNewPage}
              >
                <Box>
                  <Grid container spacing={2} ref={successRef}>
                    <PinnedContent
                      handleOpen={allowPosting ? handleOpen : undefined}
                      locationText={community.locationText}
                      posts={pinnedToDisplay}
                      showPostButton={contributionsToDisplay.length > 0}
                      pageId='community'
                    />
                    {
                      !userIsModerator && pendingContributionIds.length > 0
                        ? (
                          <Grid item xs={12}>
                            <Alert severity='info'>You have one or more posts pending community review. They will be viewable once they are approved by a community admin.</Alert>
                          </Grid>
                        )
                        : null
                    }
                  </Grid>
                  <Grid container spacing={4} sx={{ marginTop: 1 }}>
                    {contributionsToDisplay.length === 0 ? (
                      <Grid item xs={12}>
                        <FullWidthPostCTA onPostClick={allowPosting ? handleOpen : null} />
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }} id='posts'>
                          {sentenceCase(sortBy)} Posts
                        </Grid>
                        <Grid item sx={{ textAlign: 'right' }}>
                          <Select
                            id='post-sort'
                            value={sortBy}
                            onChange={handleSort}
                            defaultValue='top'
                            size='small'
                          >
                            <MenuItem value='top'>Top Posts</MenuItem>
                            <MenuItem value='new'>New Posts</MenuItem>
                          </Select>
                        </Grid>
                        <Grid item container xs={12} spacing={2}>
                          <PostGridItems
                            posts={contributionsToDisplay}
                            testimonials={community.testimonials}
                            community={community}
                            numPages={view.isLoading ? page - 1 : page}
                            onPostClick={!userIsBlocked ? handleOpen : null}
                            pageId='community'
                            suggestedTopics={suggestedTopics}
                          />
                        </Grid>
                      </>
                    )}
                    {view.isLoading && (
                      <Grid item container spacing={2} sx={{ marginBottom: '744px', paddingTop: 2 }}>
                        {[...Array(6)].map((x, i) => (
                          <Grid item xs={12} sm={6} md={4} key={`post-skeleton-${i}`} alignItems='flex-start'>
                            <Skeleton variant='rectangular' width='100%' height={260} />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </InfiniteScroll>
              <ResponsiveBottomDrawer isHidden={isFavorite}>
                <FavoriteFooterButton
                  fullWidth
                  disableElevation
                  variant='contained'
                  startIcon={<Favorite color='favorite' />}
                  onClick={handleFavoriteClick}
                >
                  Add to Favorites
                </FavoriteFooterButton>
              </ResponsiveBottomDrawer>
            </ColorBlocks.Block>
          </FullWrapper>
        </RefreshPinnedContext.Provider>
      </CommunityContext.Provider>
    </OfferProgressContext.Provider>
  );
}

export const getServerSideProps = async (context) => {
  const {
    slug = '',
    page = 1,
    qPage = 1,
    refresh = false,
  } = context.query;

  const { community, pageSettings, offerProgressSettings } = await client.fetch(`{
    'community': *[_type == 'community' && slug.current == $slug][0]{
      ...,
      'offers': *[ _type == 'incentive' && status != 'inactive' && ^._id in communities[]._ref ]{..., communities[]->{regionText}},
    },
    'pageSettings': *[_id == 'communitySettings'][0],
    'offerProgressSettings': *[_id == 'offerProgressSettings'][0]
  }`, { slug });
  if (!community) {
    return { notFound: true };
  }

  community.offers.sort((a, b) => {
    if ((a.status === b.status && a.status === 'active')
      || ![a.status, b.status].includes('active')) {
      return (b.adPacingWeight ?? 0) - (a.adPacingWeight ?? 0);
    }
    return a.status === 'active' ? -1 : 1;
  });

  const [
    topicTotals,
    hashtagTotals,
    initialContributions,
    pinnedContributions,
    initialQuestions,
    communityBlocks,
  ] = await Promise.all([
    getTopicTotals(community._id),
    getHashtagTotals(community._id),
    getPaginatedCommunityContributions(
      6,
      6 * Math.max(page - 1, 0),
      'top',
      { communityId: community._id, $or: [{ type: 'default' }, { type: null }] },
    ),
    await getPinnedContributions({
      communityId: community._id,
      $or: [{ type: 'default' }, { type: null }],
    }),
    getPaginatedCommunityContributions(
      3,
      3 * Math.max(qPage - 1, 0),
      'top',
      { communityId: community._id, type: 'question' },
    ),
    getCommunityBlocks(community._id),
  ]);

  // show suggested topics if at least 2 other topics have 4 or more posts each
  const suggestedTopics = (topicTotals?.data?.filter((topic) => topic.count >= 4));
  const suggestedHashtags = (hashtagTotals?.data?.filter((tag) => tag.count >= 4));
  const showSuggestedTopics = suggestedTopics.length >= 0 || suggestedHashtags.length >= 0;

  setCacheHeaders(context.res, 300, 3600, refresh);

  const analyticsProps = {
    pageName: 'community',
    entityKey: toKey(types.COMMUNITY, community._id),
  };
  return {
    props: {
      analyticsProps,
      community: {
        ...community,
        formattedLocationText: locationLabelText(community.locationText, community.regionText),
      },
      pageSettings,
      initialContributions,
      initialQuestions,
      pinnedContributions,
      communityBlocks,
      suggestedTopics: showSuggestedTopics ? [...suggestedTopics, ...suggestedHashtags] : null,
      offerProgressSettings,
    },
  };
};

export default CommunityPage;
